/**
 * Tenant Context Helper
 * 
 * Utilities to access and manage tenant context throughout the Oxigen application.
 * The tenant context is set by middleware based on the subdomain and stored in cookies.
 */

export interface TenantContext {
    id: string
    name: string
    subdomain: string
    baseDomain: string
    fullDomain: string
    redmineBaseUrl: string
    backendUrl: string
    isActive: boolean
    // SECURITY: Database configuration removed - all DB operations proxied through backend
    // SECURITY: Redmine API keys removed - handled by backend only
}

/**
 * Get the current tenant context from cookies
 * This is set by the middleware when accessing via subdomain
 */
export function getTenantContext(): TenantContext | null {
    if (typeof window === 'undefined') {
        return null
    }

    try {
        const cookies = document.cookie.split(';')
        const tenantCookie = cookies.find(c => c.trim().startsWith('tenant-context='))

        if (!tenantCookie) {
            return null
        }

        const cookieValue = tenantCookie.split('=').slice(1).join('=').trim()
        const tenantData = JSON.parse(decodeURIComponent(cookieValue))

        return tenantData as TenantContext
    } catch (error) {
        console.error('[Tenant Helper] Failed to parse tenant context:', error)
        return null
    }
}

/**
 * Get the Redmine base URL for the current tenant
 * Returns fallback URL if tenant context is not yet available
 */
export function getRedmineBaseUrl(): string {
    const tenant = getTenantContext()

    if (tenant?.redmineBaseUrl) {
        return tenant.redmineBaseUrl
    }

    // Fallback for when tenant context is not yet available (during initial hydration)
    return 'https://oxigen.oxinus.holdings/'
}

/**
 * Get the full domain for the current tenant
 */
export function getTenantFullDomain(): string | null {
    const tenant = getTenantContext()
    return tenant?.fullDomain || null
}

/**
 * Check if we're running in a tenant context
 */
export function hasTenantContext(): boolean {
    return getTenantContext() !== null
}

/**
 * REMOVED FOR SECURITY: getTenantDatabaseConfig()
 *
 * Database configuration is NO LONGER accessible from the frontend.
 * All database operations must be proxied through the backend API.
 *
 * Migration guide:
 * - Replace direct DB queries with backend API calls
 * - Use endpoints like: /api/tenants/:id/database/stats
 * - Backend performs queries and returns only results
 */

/**
 * Validate that tenant context is available
 * Throws an error if not (useful for pages that require tenant)
 */
export function requireTenantContext(): TenantContext {
    const tenant = getTenantContext()

    if (!tenant) {
        throw new Error('Tenant context is required. Please access via tenant subdomain (e.g., tenant.domain.com)')
    }

    if (!tenant.isActive) {
        throw new Error(`Tenant "${tenant.name}" is not active`)
    }

    return tenant
}

/**
 * Log tenant context information (for debugging)
 */
export function logTenantContext(): void {
    const tenant = getTenantContext()

    if (!tenant) {
        console.log('[Tenant Context] No tenant context found')
        return
    }

    console.log('[Tenant Context]', {
        name: tenant.name,
        subdomain: tenant.subdomain,
        baseDomain: tenant.baseDomain,
        fullDomain: tenant.fullDomain,
        redmineUrl: tenant.redmineBaseUrl,
        isActive: tenant.isActive,
    })
}
